import { ChangeEvent, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { RetailBuyerSelector } from 'src/components/Selectors/BuyerSelector';
import { CurrencyFilterSelector } from 'src/components/Selectors/CurrencyFilterSelector';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { ContentId } from 'src/utils/constants/contentId';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { BuyerWithSalesDto, ListingDetails } from 'src/WebApiController';

import { useRetailSaleContext } from '../RetailSaleLocalContext';
import { RetailSaleFormValues } from '../types';
import {
  AddNewBuyerForm,
  SaleTags,
  TicketSection,
} from './RetailSaleBuyerStep.components';
import * as styles from './RetailSaleBuyerStep.css';

type RetailSaleBuyerStepProps = {
  listing?: ListingDetails;
};

export const RetailSaleBuyerStep = ({ listing }: RetailSaleBuyerStepProps) => {
  const { register, setValue, watch } = useFormContext<RetailSaleFormValues>();
  const { getUiCurrency } = useLocalizationContext();

  const { selectedBuyer, setSelectedBuyer } = useRetailSaleContext();

  const optionalMsg = useContent(ContentId.Optional);
  const detailsMsg = useContent(ContentId.Details);
  const externalSaleIdMsg = useContent(ContentId.ExternalSaleId);
  const addNewBuyerMsg = useContent(ContentId.AddNewBuyer);

  const currencyCode = watch('currencyCode');
  const totalNetProceeds = watch('totalNetProceeds');
  const saleDate = watch('saleDate');
  const inHandDate = watch('inHandDate');

  const uiCurrency = getUiCurrency(currencyCode);

  const handleBuyerChange = useCallback(
    (buyer?: BuyerWithSalesDto) => {
      setSelectedBuyer(buyer);
      if (buyer) {
        setValue('buyerName', buyer.buyerName ?? undefined);
        setValue('buyerEmail', buyer.buyerEmail);
        setValue('buyerPhone', buyer.buyerPhoneNumber ?? undefined);
        setValue('shippingStreet', buyer.shippingAddressLine1 ?? undefined);
        setValue('shippingSuite', buyer.shippingAddressLine2 ?? undefined);
        setValue('shippingCity', buyer.shippingCity ?? undefined);
        setValue('shippingState', buyer.shippingState ?? undefined);
        setValue('shippingZipCode', buyer.shippingZipCode ?? undefined);
        setValue('shippingCountry', buyer.shippingCountry ?? undefined);
        setValue('billingStreet', buyer.billingAddressLine1 ?? undefined);
        setValue('billingSuite', buyer.billingAddressLine2 ?? undefined);
        setValue('billingCity', buyer.billingCity ?? undefined);
        setValue('billingState', buyer.billingState ?? undefined);
        setValue('billingZipCode', buyer.billingZipCode ?? undefined);
        setValue('billingCountry', buyer.billingCountry ?? undefined);
      }
    },
    [setSelectedBuyer, setValue]
  );

  const shouldShowBuyerForm = useMemo(() => {
    return (
      !selectedBuyer ||
      !selectedBuyer.shippingAddressLine1 ||
      !selectedBuyer.shippingCity ||
      !selectedBuyer.shippingState ||
      !selectedBuyer.shippingZipCode ||
      !selectedBuyer.shippingCountry ||
      !selectedBuyer.billingAddressLine1 ||
      !selectedBuyer.billingCity ||
      !selectedBuyer.billingState ||
      !selectedBuyer.billingZipCode ||
      !selectedBuyer.billingCountry
    );
  }, [selectedBuyer]);

  return (
    <Stack direction="column" gap="m">
      {/* RETAIL CUSTOMER section */}
      <DetailSection name={<Content id={ContentId.RetailCustomer} />}>
        <SectionContent numOfColumns={1}>
          <PosFormField label={<Content id={ContentId.ChooseBuyer} />}>
            <RetailBuyerSelector
              value={selectedBuyer?.buyerEmail ?? 'AddNewBuyer'}
              onBuyerSelected={handleBuyerChange}
              className={styles.fullWidth}
              additionalOptions={{ AddNewBuyer: addNewBuyerMsg }}
              additionalOptionsPosition="top"
            />
          </PosFormField>
        </SectionContent>
        {shouldShowBuyerForm && <AddNewBuyerForm />}
      </DetailSection>

      {/* DETAIL section */}
      <DetailSection name={detailsMsg}>
        <SectionContent numOfColumns={2}>
          <PosFormField label={externalSaleIdMsg}>
            <PosTextField
              disabled
              placeholder={optionalMsg}
              {...register('externalSaleId')}
            />
          </PosFormField>
          <div></div>
          <PosFormField label={<Content id={ContentId.SaleDate} />}>
            <DatePickerInput
              date={saleDate ? new Date(saleDate) : undefined}
              onDateChange={(date: Date) => {
                if (date) {
                  setValue('saleDate', date.toISOString());
                }
              }}
              locale={getLocaleFromLanguageOrCurrent()}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.InHandDate} />}>
            <DatePickerInput
              date={inHandDate ? new Date(inHandDate) : undefined}
              onDateChange={(date: Date) => {
                if (date) {
                  setValue('inHandDate', date.toISOString());
                }
              }}
              locale={getLocaleFromLanguageOrCurrent()}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.CurrencyCode} />}>
            <CurrencyFilterSelector
              value={uiCurrency.code}
              className={styles.fullWidth}
              defaultValue={currencyCode}
              onChange={(newCurrencyCode) => {
                if (newCurrencyCode) {
                  setValue('currencyCode', newCurrencyCode);
                }
              }}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.TotalNetProceeds} />}>
            <PosCurrencyField
              uiCurrency={uiCurrency}
              value={totalNetProceeds ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const v = parseFloat(e.target.value) || 0;
                if (v >= 0 && v <= Number.MAX_VALUE) {
                  setValue('totalNetProceeds', v);
                }
              }}
            />
          </PosFormField>
        </SectionContent>
        <SectionContent numOfColumns={1}>
          <PosFormField label={<Content id={ContentId.PrivateNote} />}>
            <PosTextArea
              placeholder={optionalMsg}
              {...register('privateNotes')}
            />
          </PosFormField>
        </SectionContent>
      </DetailSection>

      {/* ALLOCATED LISTING section */}
      <TicketSection
        maxQuantity={listing?.availQty}
        deliveryType={listing?.delivType}
        tickets={listing?.tickets ?? []}
        listing={listing}
      />

      {/* TAGS section */}
      <DetailSection name={<Content id={ContentId.Tags} />}>
        <SaleTags />
      </DetailSection>
    </Stack>
  );
};
